import React from "react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import { ImgTimeTaken } from "./Images/ImgTimeTaken.tsx";
import type { Message } from "../models.ts";

export interface MessageStatsProps {
  message: Message;
  isGenerating?: boolean;
}

@observer
export class MessageStats extends React.Component<MessageStatsProps> {
  @observable private _showTimeTakenPopup: boolean = false;
  @observable private _now: number = Date.now();
  private _intervalId: ReturnType<typeof setInterval> | null = null;

  constructor(props: MessageStatsProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    document.addEventListener('click', this._onDocumentClickTimeTaken);
    this._syncInterval();
  }

  componentDidUpdate(prevProps: MessageStatsProps) {
    if (prevProps.isGenerating !== this.props.isGenerating) {
      this._syncInterval();
    }
  }

  componentWillUnmount(): void {
    document.removeEventListener('click', this._onDocumentClickTimeTaken);
    if (this._intervalId !== null) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  private _syncInterval() {
    if (this.props.isGenerating) {
      if (this._intervalId === null) {
        this._intervalId = setInterval(action(() => { this._now = Date.now(); }), 100);
      }
    } else {
      if (this._intervalId !== null) {
        clearInterval(this._intervalId);
        this._intervalId = null;
      }
    }
  }

  render() {
    const { message, isGenerating } = this.props;

    // Preceding time: accumulated tool execution durations from prior iterations.
    // Skipped for collapsed messages because their timeTaken already sums all contributors.
    const precedingMs = (!message.collapsedMessages && message._precedingMs) ? message._precedingMs : 0;

    // Current LLM call time (live counter or Ollama's elapsed_ms on done).
    let currentCallSec: number;
    if (isGenerating && message._fetchStartMs !== undefined) {
      currentCallSec = (this._now - message._fetchStartMs) / 1000;
    } else {
      currentCallSec = (message.timeTaken || 0) / 1_000_000_000;
    }

    // Total time shown in badge = current call + all prior iterations
    const timeSec = currentCallSec + precedingMs / 1000;

    // Thinking time: live during thinking phase, fixed once done.
    let thinkSec: number | null;
    if (isGenerating && message._thinkingStartMs !== undefined && message.thinkingTimeTaken == null) {
      thinkSec = (this._now - message._thinkingStartMs) / 1000;
    } else {
      thinkSec = message.thinkingTimeTaken != null ? message.thinkingTimeTaken / 1000 : null;
    }

    // TPS/genSec: based on current call only (tokens belong to this LLM call)
    const genSec = thinkSec != null ? Math.max(0, currentCallSec - thinkSec) : currentCallSec;
    const tps = (message.tokensOut && genSec > 0) ? (message.tokensOut / genSec) : null;

    return (
      <span className="message-time-taken message-time-taken--clickable" onClick={this._onTimeTakenClick}>
        <ImgTimeTaken size={12} /> {timeSec.toFixed(1)}s
        {this._showTimeTakenPopup && (
          <div className="tc-popup tt-popup" onClick={e => e.stopPropagation()}>
            <div className="tc-popup-title">Response stats</div>
            {tps != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Tokens / sec</span>
                <span className="tc-popup-value tt-value-highlight">{tps.toFixed(1)}</span>
              </div>
            )}
            <div className="tc-popup-row">
              <span className="tc-popup-label">Total time</span>
              <span className="tc-popup-value">{timeSec.toFixed(2)}s</span>
            </div>
            {precedingMs > 0 && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">LLM time</span>
                <span className="tc-popup-value">{currentCallSec.toFixed(2)}s</span>
              </div>
            )}
            {thinkSec != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Thinking time</span>
                <span className="tc-popup-value">{thinkSec.toFixed(2)}s</span>
              </div>
            )}
            {thinkSec != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Generation time</span>
                <span className="tc-popup-value">{genSec.toFixed(2)}s</span>
              </div>
            )}
            {message.tokensIn != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Tokens in</span>
                <span className="tc-popup-value">{message.tokensIn.toLocaleString()}</span>
              </div>
            )}
            {message.tokensOut != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Tokens out</span>
                <span className="tc-popup-value">{message.tokensOut.toLocaleString()}</span>
              </div>
            )}
            {message.tokensIn != null && message.tokensOut != null && (
              <div className="tc-popup-row">
                <span className="tc-popup-label">Total tokens</span>
                <span className="tc-popup-value">{(message.tokensIn + message.tokensOut).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </span>
    );
  }
  
  private _onTimeTakenClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    this._showTimeTakenPopup = !this._showTimeTakenPopup;
  }
  
  private _onDocumentClickTimeTaken = () => {
    if (this._showTimeTakenPopup) this._showTimeTakenPopup = false;
  }
}
